import type { ApiNode } from '@goalbuddy/client';
import { canHaveParent, NODE_TYPES, type NodeType } from '@goalbuddy/core';
import {
  closestCenter,
  DndContext,
  MeasuringStrategy,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation } from '@tanstack/react-query';
import { ArrowUp, ChevronRight, Info, ListTree } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { NodeRow, DragHandle } from '@/components/node-row';
import { TypeIcon } from '@/components/node-meta';
import { Button, Empty, Input, ProgressBar, Select, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useDetail, useNode, useNodes, useStructure } from '@/lib/hooks';
import { ancestorsOf } from '@/lib/tree';
import { areaColor } from '@/lib/area';
import { cn } from '@/lib/utils';

type DropMode = { overId: number; mode: 'into' | 'reorder' } | null;

function SortableRow({ node, dropInto, depth = 0 }: { node: ApiNode; dropInto: boolean; depth?: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition, paddingLeft: depth * 20 }}
      className={cn(isDragging && 'opacity-40', dropInto && 'rounded-xl ring-2 ring-primary')}
    >
      <NodeRow node={node} dragHandle={<DragHandle {...attributes} {...listeners} />} />
    </div>
  );
}

function ParentDropZone({ parent }: { parent: ApiNode | null }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'parent-drop' });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'mb-2 flex items-center gap-2 rounded-xl border border-dashed border-border p-2 text-xs text-muted-fg',
        isOver && 'border-primary bg-accent text-primary',
      )}
    >
      <ArrowUp size={13} /> Drop here to move up to {parent ? parent.title : 'top level / inbox'}
    </div>
  );
}

function AddChild({ parent }: { parent: ApiNode }) {
  const allowed = NODE_TYPES.filter((t) => canHaveParent(t, parent.type));
  const [type, setType] = useState<NodeType>(allowed.includes('task') ? 'task' : allowed[0]!);
  const [title, setTitle] = useState('');
  const create = useMutation({
    mutationFn: () => api.createNode({ type, title: title.trim(), parent_id: parent.id }),
    onSuccess: () => setTitle(''),
  });
  if (allowed.length === 0) return null;
  return (
    <form
      className="mt-4 flex gap-2 rounded-xl border border-border bg-surface p-2 shadow-[var(--shadow-card)]"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) create.mutate();
      }}
    >
      <Select
        value={type}
        onChange={(e) => setType(e.target.value as NodeType)}
        className="w-28 border-transparent bg-muted"
        aria-label="Type"
      >
        {allowed.map((t) => (
          <option key={t}>{t}</option>
        ))}
      </Select>
      <Input
        className="border-transparent"
        placeholder={`Add ${type} to ${parent.title}…`}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <Button type="submit" disabled={!title.trim()}>
        Add
      </Button>
    </form>
  );
}

/** Flattens children; inside a project, tasks under sections are shown indented below their section. */
function flatten(
  children: ApiNode[],
  all: ApiNode[],
  isProject: boolean,
): { node: ApiNode; depth: number }[] {
  if (!isProject) return children.map((node) => ({ node, depth: 0 }));
  const out: { node: ApiNode; depth: number }[] = [];
  const loose = children.filter((c) => c.type !== 'section');
  const sections = children.filter((c) => c.type === 'section');
  for (const n of loose) out.push({ node: n, depth: 0 });
  for (const s of sections) {
    out.push({ node: s, depth: 0 });
    for (const t of all.filter((a) => a.parent_id === s.id).sort((a, b) => a.sort - b.sort))
      out.push({ node: t, depth: 1 });
  }
  return out;
}

export function NodePage() {
  const id = Number(useParams().id);
  const { data: node, isLoading } = useNode(id);
  const [showDone, setShowDone] = useState(false);
  const { data: children } = useNodes({ parent: id, include_done: showDone });
  const isProject = node?.type === 'project';
  const { data: sectionKids } = useNodes({ type: ['task', 'idea'], include_done: showDone }, isProject);
  const { data: structure } = useStructure();
  const { open } = useDetail();
  const { data: allKids } = useNodes({ parent: id, include_done: true });
  const [drop, setDrop] = useState<DropMode>(null);
  const [dragging, setDragging] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const move = useMutation({
    mutationFn: (v: { id: number; parent: number | null; sort?: number }) =>
      api.moveNode(v.id, v.parent, v.sort),
  });

  if (isLoading || !node) return <Spinner />;
  const rows = flatten(children ?? [], sectionKids ?? [], isProject);
  const byId = new Map(rows.map((r) => [r.node.id, r.node]));
  const crumbs = structure ? ancestorsOf(structure, node.id) : [];
  const parent = crumbs.at(-1) ?? null;

  const modeFor = (e: DragMoveEvent | DragEndEvent): DropMode => {
    const over = e.over;
    const rect = e.active.rect.current.translated;
    if (!over || !rect || over.id === 'parent-drop' || over.id === e.active.id) return null;
    const center = rect.top + rect.height / 2;
    const band = over.rect.height * 0.3;
    const mid = over.rect.top + over.rect.height / 2;
    return { overId: Number(over.id), mode: Math.abs(center - mid) < band ? 'into' : 'reorder' };
  };

  const onDragEnd = (e: DragEndEvent) => {
    setDrop(null);
    const activeId = Number(e.active.id);
    const active = byId.get(activeId);
    if (!active) return;
    if (e.over?.id === 'parent-drop') {
      return move.mutate({ id: activeId, parent: node.parent_id });
    }
    const m = modeFor(e);
    if (!m) return;
    const target = byId.get(m.overId);
    if (!target) return;
    if (m.mode === 'into') {
      if (!canHaveParent(active.type, target.type))
        return toast.error(`A ${active.type} cannot go under a ${target.type}`);
      return move.mutate({ id: activeId, parent: target.id });
    }
    // Reorder among the target's siblings (may also move between sections of a project).
    const siblings = rows
      .filter((r) => r.node.parent_id === target.parent_id && r.node.id !== activeId)
      .map((r) => r.node);
    const index = siblings.findIndex((s) => s.id === target.id);
    const rect = e.active.rect.current.translated!;
    const after = rect.top > e.over!.rect.top;
    if (
      target.parent_id !== active.parent_id &&
      !canHaveParent(active.type, byId.get(target.parent_id ?? -1)?.type ?? node.type)
    )
      return;
    move.mutate({ id: activeId, parent: target.parent_id, sort: index + (after ? 1 : 0) });
  };

  const total = (allKids ?? []).filter((k) => k.type !== 'section').length;
  const doneKids = (allKids ?? []).filter((k) => k.status === 'done').length;
  const color = areaColor(structure, node.id);
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-1 text-xs text-muted-fg">
        {crumbs.map((c) => (
          <span key={c.id} className="flex items-center gap-1">
            <Link to={`/node/${c.id}`} className="hover:text-fg hover:underline">
              {c.title}
            </Link>
            <ChevronRight size={12} />
          </span>
        ))}
      </div>
      <header className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2.5 text-2xl font-semibold">
              <TypeIcon type={node.type} size={20} />{' '}
              <span className="break-words" style={color ? { textDecorationColor: color } : undefined}>
                {node.title}
              </span>
            </h1>
            <div className="mt-1 text-sm text-muted-fg">
              {node.description_short ?? `${node.type}${node.period ? ` · ${node.period}` : ''}`}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-sm text-muted-fg">
              <input
                type="checkbox"
                className="accent-[var(--color-primary)]"
                checked={showDone}
                onChange={(e) => setShowDone(e.target.checked)}
              />{' '}
              Show completed
            </label>
            <Button variant="outline" size="sm" onClick={() => open(node.id)}>
              <Info size={13} /> Details
            </Button>
          </div>
        </div>
        {total > 0 && (
          <div className="mt-3 flex items-center gap-3">
            <ProgressBar value={doneKids / total} className="max-w-xs flex-1" />
            <span className="text-xs text-muted-fg">
              {doneKids} of {total} done
            </span>
          </div>
        )}
      </header>
      {node.open_question && (
        <div className="mb-4 rounded-xl border border-warn/40 bg-warn/5 p-3 text-sm">
          <span className="font-medium">Open question:</span> {node.open_question}
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={() => setDragging(true)}
        onDragMove={(e) => setDrop(modeFor(e))}
        onDragEnd={(e) => {
          setDragging(false);
          onDragEnd(e);
        }}
        onDragCancel={() => {
          setDragging(false);
          setDrop(null);
        }}
      >
        {dragging && <ParentDropZone parent={parent} />}
        <SortableContext items={rows.map((r) => r.node.id)} strategy={verticalListSortingStrategy}>
          {rows.length === 0 ? (
            <Empty icon={<ListTree size={20} />}>Nothing here yet. Add the first item below.</Empty>
          ) : (
            <div className="space-y-0.5">
              {rows.map((r) => (
                <SortableRow
                  key={r.node.id}
                  node={r.node}
                  depth={r.depth}
                  dropInto={drop?.mode === 'into' && drop.overId === r.node.id}
                />
              ))}
            </div>
          )}
        </SortableContext>
      </DndContext>
      <p className="mt-3 text-xs text-muted-fg">
        Drag the handle to reorder. Drop onto the middle of a row to move inside it.
      </p>
      <AddChild parent={node} />
    </div>
  );
}
